import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Avatar,
  CircularProgress,
  Typography,
  Checkbox,
} from "@mui/material";
import AccountIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";

const AccountsSelect = ({
  contentType,
  platforms,
  loadingAccounts,
  availableAccounts,
  selectedAccounts,
  socialMediaAccounts,
  handleAccountChange,
  removeAccount,
}) => {
  const allAccountIds = availableAccounts.map((a) => a.accountId);
  const allSelected =
    allAccountIds.length > 0 &&
    allAccountIds.every((id) => selectedAccounts.includes(id));

  // Intercept the __select_all__ sentinel value
  const _handleChange = (e) => {
    const { value } = e.target;
    if (Array.isArray(value) && value.includes("__select_all__")) {
      handleAccountChange({
        target: { value: allSelected ? [] : allAccountIds },
      });
    } else {
      handleAccountChange(e);
    }
  };

  return (
    <>
      {/* Quick Select All / Clear All bar */}
      {availableAccounts.length > 0 && !loadingAccounts && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.75,
            px: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
            {selectedAccounts.length} / {availableAccounts.length} selected
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="Select All"
              size="small"
              icon={<SelectAllIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() =>
                handleAccountChange({
                  target: { value: allSelected ? [] : allAccountIds },
                })
              }
              disabled={allSelected || platforms.length === 0 || !contentType}
              sx={{
                cursor: "pointer",
                bgcolor: allSelected ? "#dcfce7" : "#e0f2fe",
                color: allSelected ? "#15803d" : "#0277bd",
                fontWeight: 600,
                fontSize: "0.72rem",
                "&:hover": { bgcolor: "#bae6fd" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            />
            <Chip
              label="Clear All"
              size="small"
              icon={<ClearAllIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() =>
                handleAccountChange({ target: { value: [] } })
              }
              disabled={selectedAccounts.length === 0}
              sx={{
                cursor: "pointer",
                bgcolor: "#fef2f2",
                color: "#dc2626",
                fontWeight: 600,
                fontSize: "0.72rem",
                "&:hover": { bgcolor: "#fee2e2" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            />
          </Box>
        </Box>
      )}

      <FormControl
        fullWidth
        sx={{ mb: 2 }}
        error={selectedAccounts.length === 0}
        disabled={platforms.length === 0 || loadingAccounts || !contentType}
      >
        <InputLabel>Select Social Media Accounts *</InputLabel>
        <Select
          multiple
          required
          value={selectedAccounts}
          onChange={_handleChange}
          label="Select Social Media Accounts *"
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                minHeight: "40px",
                alignItems: "center",
              }}
            >
              {selected.length >= 4 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={
                      allSelected
                        ? `All ${selected.length} accounts selected`
                        : `${selected.length} accounts selected`
                    }
                    sx={{
                      bgcolor: "#e8f5e8",
                      color: "#2e7d32",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                    avatar={<SelectAllIcon sx={{ color: "#2e7d32 !important" }} />}
                  />
                  <Chip
                    label="Clear all"
                    size="small"
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleAccountChange({ target: { value: [] } });
                    }}
                    sx={{
                      bgcolor: "#fef2f2",
                      color: "#dc2626",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#fee2e2" },
                    }}
                    deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "#dc2626 !important" }} />}
                    onDelete={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleAccountChange({ target: { value: [] } });
                    }}
                  />
                </Box>
              ) : (
                selected.map((accountId) => {
                  const account = socialMediaAccounts.find(
                    (acc) => acc.accountId === accountId
                  );
                  return (
                    <Box
                      key={accountId}
                      sx={{ position: "relative", display: "inline-flex" }}
                      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    >
                      <Chip
                        label={`${account?.accountName || accountId} (${
                          account?.provider === "Twitter"
                            ? "Twitter (X)"
                            : account?.provider || "Unknown"
                        })`}
                        sx={{
                          bgcolor: "#e8f5e8",
                          color: "#2e7d32",
                          "& .MuiChip-deleteIcon": {
                            color: "#2e7d32",
                            "&:hover": { color: "#ef4444" },
                          },
                        }}
                        avatar={<AccountIcon />}
                        onDelete={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          removeAccount(accountId);
                        }}
                        deleteIcon={
                          <CloseIcon sx={{ fontSize: "16px !important", cursor: "pointer" }} />
                        }
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          )}
          sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
        >
          {loadingAccounts ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              Loading accounts...
            </MenuItem>
          ) : availableAccounts.length === 0 ? (
            <MenuItem disabled>
              {!contentType
                ? "Please select content type first"
                : platforms.length === 0
                ? "Please select platforms first"
                : "No accounts available for selected platforms"}
            </MenuItem>
          ) : (
            [
              <MenuItem
                key="__select_all__"
                value="__select_all__"
                sx={{ borderBottom: "1px solid #e2e8f0", mb: 0.5 }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedAccounts.length > 0 && !allSelected}
                  sx={{
                    color: "#4B5AB6",
                    "&.Mui-checked": { color: "#4B5AB6" },
                    "&.MuiCheckbox-indeterminate": { color: "#4B5AB6" },
                    p: 0.5,
                    mr: 1,
                  }}
                />
                <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.875rem" }}>
                  {allSelected ? "Deselect All" : `Select All (${allAccountIds.length})`}
                </Typography>
              </MenuItem>,
              ...availableAccounts.map((account) => (
                <MenuItem key={account.accountId} value={account.accountId}>
                  <Checkbox
                    checked={selectedAccounts.includes(account.accountId)}
                    sx={{
                      color: "#4B5AB6",
                      "&.Mui-checked": { color: "#4B5AB6" },
                      p: 0.5,
                      mr: 1,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#4B5AB6" }}>
                      {account.provider.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {account.accountName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {account.provider === "Twitter"
                          ? "Twitter (X)"
                          : account.provider}{" "}
                        • {account.accountId} • {account.status}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        account.provider === "Twitter"
                          ? "Twitter (X)"
                          : account.provider
                      }
                      size="small"
                      sx={{
                        bgcolor: "#e0e7ff",
                        color: "#4B5AB6",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </MenuItem>
              )),
            ]
          )}
        </Select>

        {selectedAccounts.length === 0 && (
          <FormHelperText>
            {!contentType
              ? "Please select content type first"
              : platforms.length === 0
              ? "Please select platforms first"
              : "Please select at least one social media account"}
          </FormHelperText>
        )}
        {selectedAccounts.length > 0 && (
          <FormHelperText sx={{ color: "#059669", fontWeight: 500 }}>
            💡 Click the ✕ icon on any account chip to remove it quickly
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export default AccountsSelect;

